import {Resource, ResourceLoadContext } from "./resource";
import { TextureResource } from "./textureresource";
import MeshResource from "./mesh_resource";
import { SoundResource}  from "./soundresource";
import ShaderService from "../shader/shader_service";
import Stage from "../stage/stage";

const GLB_RANGE_SIZE = 8 * 1024 * 1024;

async function fetchGlbInRanges(url: string, path: string): Promise<Response> {
  const first = await fetch(url, {
    cache: "no-store",
    headers: { Range: `bytes=0-${GLB_RANGE_SIZE - 1}` },
  });

  if (first.status === 200) {
    return first;
  }
  if (first.status !== 206) {
    throw new Error(`Failed to fetch resource range: ${path} (${first.status})`);
  }

  const match = /^bytes 0-(\d+)\/(\d+)$/.exec(first.headers.get("content-range") ?? "");
  if (!match) {
    throw new Error(`Missing or invalid Content-Range for resource: ${path}`);
  }

  const firstEnd = Number(match[1]);
  const totalLength = Number(match[2]);
  const chunks: ArrayBuffer[] = [await first.arrayBuffer()];

  for (let start = firstEnd + 1; start < totalLength; start += GLB_RANGE_SIZE) {
    const end = Math.min(start + GLB_RANGE_SIZE - 1, totalLength - 1);
    const response = await fetch(url, {
      cache: "no-store",
      headers: { Range: `bytes=${start}-${end}` },
    });
    if (response.status !== 206) {
      throw new Error(`Failed to fetch resource range: ${path} (${response.status})`);
    }

    const bytes = await response.arrayBuffer();
    if (bytes.byteLength !== end - start + 1) {
      throw new Error(`Resource range length mismatch: ${path}`);
    }
    chunks.push(bytes);
  }

  const body = new Blob(chunks, { type: first.headers.get("content-type") ?? "model/gltf-binary" });
  if (body.size !== totalLength) {
    throw new Error(`Resource length mismatch after range assembly: ${path}`);
  }

  return new Response(body, {
    status: 200,
    headers: { "Content-Type": body.type },
  });
}

export default class ResourceService 
{
  private ctx?: ResourceLoadContext;
  private readonly loaded = new Map<string, Resource<unknown>>();
  private readonly inflight = new Map<string, Promise<Resource<unknown>>>();

  constructor() {}

  createContext(device: GPUDevice): void {
    this.ctx = {
      device,

      async fetchPrivate(path: string): Promise<Response> {
        const url = `/api/resource/${encodeURIComponent(path)}`;
        if (path.endsWith(".glb")) {
          return fetchGlbInRanges(url, path);
        }

        const res = await fetch(url, { cache: "no-store" });
        if (!res.ok) {
          throw new Error(`Failed to fetch resource: ${path}`);
        }

        return res;
      },
    };
  }

  async getTexture(path: string): Promise<TextureResource> {
    const ctx = this.requireContext();
    const cacheKey = `texture:${path}`;
    return this.getOrLoad(ctx, cacheKey, () => new TextureResource(path));
  }
    async getSound(path: string): Promise<SoundResource> {
    const ctx = this.requireContext();
    const cacheKey = `sound:${path}`;
    return this.getOrLoad(ctx, cacheKey, () => new SoundResource(path));
  }

  async getMesh(path: string): Promise<MeshResource> {
    const ctx = this.requireContext();
    const cacheKey = `mesh:${path}`;
    return this.getOrLoad(ctx, cacheKey, () => new MeshResource(path));
}

  disposeAll(): void {
    const ctx = this.requireContext();

    for (const resource of this.loaded.values()) {
      resource.dispose(ctx);
    }

    this.loaded.clear();
    this.inflight.clear();
  }

  private requireContext(): ResourceLoadContext 
  {
    if (!this.ctx)
    {
      const device= Stage.instance().shader_service.get_device();
      if(device)
      {
        this.createContext(device);
        if(this.ctx)
        {
          return this.ctx;
        }
      }
         throw new Error("ResourceService has not been initialized. Call createContext(device) first.");
    }
    return this.ctx;
  }

  private async getOrLoad<TResource extends Resource<unknown>>(
    ctx: ResourceLoadContext,
    cacheKey: string,
    factory: () => TResource
  ): Promise<TResource> {
    const existing = this.loaded.get(cacheKey);
    if (existing) {
      return existing as TResource;
    }

    const pending = this.inflight.get(cacheKey);
    if (pending) {
      return pending as Promise<TResource>;
    }

    const resource = factory();

    const promise = resource.load(ctx)
      .then(() => {
        this.loaded.set(cacheKey, resource);
        this.inflight.delete(cacheKey);
        return resource;
      })
      .catch((error) => {
        this.inflight.delete(cacheKey);
        throw error;
      });

    this.inflight.set(cacheKey, promise);
    return promise as Promise<TResource>;
  }
}